import { lazy, Suspense, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { Navigate, Route, Routes, useLocation, useNavigate, useParams } from "react-router-dom";
import { BookingProvider } from "@/state/BookingContext";
import { DashboardRouteSkeleton } from "@/features/dashboard/DashboardRouteSkeleton";
import { LandingPage } from "@/pages/LandingPage";
import { setAccessToken } from "@/lib/apiClient";
import { buildSignInUrl, consumeAuthIntent, getCurrentRelativeUrl, peekAuthIntent, resolvePostLoginPath } from "@/lib/authRedirect";
import { AuthProvider, getMeQueryOptions, ME_QUERY_KEY, useAuth } from "@/state/AuthContext";
import { UpgradePromptProvider } from "@/state/UpgradePromptContext";
import { UpgradePromptHost } from "@/features/billing/UpgradePromptHost";
import { IntegrationProvider } from "@/state/IntegrationContext";
import { CreateEventProvider } from "@/state/CreateEventContext";
import { oauthDebug, routeDebug } from "@/lib/authDebug";
import { trackEvent, trackPageView } from "@/lib/analytics";
import { buildSignUpEvent } from "@/lib/conversionEvents";
import { GlobalApiLoader } from "@/components/GlobalApiLoader";
import { endTransitionActivity, startTransitionActivity } from "@/lib/networkActivity";
import { ONBOARDING_ENTRY_ACTIVITY } from "@/routing/routeSetupPolicy";
import { CookieConsent } from "@/components/CookieConsent";
import { api } from "@/services";
import { FIRST_TIME_ONBOARDING_ENABLED, ONBOARDING_QUERY_KEY } from "@/features/onboarding/onboardingState";
import { getBlockingRequirements } from "@/routing/routeSetupPolicy";
import { BunnyMark } from "@/components/BunnyMark";
import { BrandWordmark } from "@/components/BrandWordmark";

/* Signed-in pages, code-split out of the initial bundle.
 *
 * These were static imports, which meant every visitor -- including a logged-out one
 * landing on "/" -- downloaded and parsed the entire dashboard before the page could
 * render. The cost was mostly CSS: DashboardPage imports dashboard.css (211 KB), and
 * because nothing was split, Rollup folded it together with billing.css,
 * experiences-hub.css and create-event.css into the single render-blocking
 * main-*.css. That was ~320 KB of styles for surfaces a visitor cannot reach without
 * signing in, sitting in front of the landing hero. Lighthouse attributed 49.8 KB of
 * "unused CSS" and 136 KB of unused JS to exactly this.
 *
 * Signed-in routes and non-homepage public routes are split. Keeping only the landing
 * route synchronous materially reduces its first download; direct visits to another
 * public route get the small branded Suspense fallback below while that route arrives.
 * Protected-route chunk fetches overlap the existing auth round trip. React.lazy needs
 * a default export and these modules use named ones, so each import is mapped rather
 * than changing their export style.
 */
const DashboardPage = lazy(() =>
  import("@/pages/DashboardPage").then((m) => ({ default: m.DashboardPage }))
);
const OnboardingPage = lazy(() =>
  import("@/pages/OnboardingPage").then((m) => ({ default: m.OnboardingPage }))
);
const CreateEventSuccessPage = lazy(() =>
  import("@/pages/CreateEventSuccessPage").then((m) => ({ default: m.CreateEventSuccessPage }))
);
const AcceptInvitationPage = lazy(() =>
  import("@/pages/teams/AcceptInvitationPage").then((m) => ({ default: m.AcceptInvitationPage }))
);

// Public routes are split as well so the homepage no longer downloads their components and CSS
// before it can paint. Direct visits get a small branded fallback instead of a blank Suspense
// boundary; the landing route itself remains synchronous.
const BookingPage = lazy(() =>
  import("@/pages/BookingPage").then((m) => ({ default: m.BookingPage }))
);
const GuestManageBookingPage = lazy(() =>
  import("@/pages/guest-booking/GuestManageBookingPage").then((m) => ({ default: m.GuestManageBookingPage }))
);
const PricingPage = lazy(() =>
  import("@/pages/PricingPage").then((m) => ({ default: m.PricingPage }))
);
const CalendlyAlternativePage = lazy(() =>
  import("@/pages/CalendlyAlternativePage").then((m) => ({ default: m.CalendlyAlternativePage }))
);
const SwitchFromCalendlyPage = lazy(() =>
  import("@/pages/SwitchFromCalendlyPage").then((m) => ({ default: m.SwitchFromCalendlyPage }))
);
const RoundRobinSchedulingPage = lazy(() =>
  import("@/pages/RoundRobinSchedulingPage").then((m) => ({ default: m.RoundRobinSchedulingPage }))
);
const PrivacyPolicyPage = lazy(() =>
  import("@/pages/PrivacyPolicyPage").then((m) => ({ default: m.PrivacyPolicyPage }))
);
const TermsPage = lazy(() =>
  import("@/pages/TermsPage").then((m) => ({ default: m.TermsPage }))
);
const ContactPage = lazy(() =>
  import("@/pages/ContactPage").then((m) => ({ default: m.ContactPage }))
);
const ZoomIntegrationDocsPage = lazy(() =>
  import("@/pages/ZoomIntegrationDocsPage").then((m) => ({ default: m.ZoomIntegrationDocsPage }))
);
const LoginPage = lazy(() =>
  import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage }))
);
const NotFoundPage = lazy(() =>
  import("@/pages/NotFoundPage").then((m) => ({ default: m.NotFoundPage }))
);

function PublicRouteFallback() {
  return (
    <div className="public-route-fallback" role="status" aria-label="Loading BunnyCal">
      <span className="public-route-fallback-brand">
        <BunnyMark size={34} />
        <BrandWordmark />
      </span>
    </div>
  );
}

function PublicBookingRoute() {
  const { username, eventTypeSlug } = useParams<{ username: string; eventTypeSlug: string }>();
  if (!username || !eventTypeSlug) return <LandingPage />;
  return (
    <BookingProvider>
      <BookingPage username={username} eventTypeSlug={eventTypeSlug} />
    </BookingProvider>
  );
}

function ProtectedAppProviders({ children }: { children: JSX.Element }) {
  return (
    <IntegrationProvider>
      <CreateEventProvider>
        {children}
      </CreateEventProvider>
    </IntegrationProvider>
  );
}

/**
 * Paths the OAuth flow can dump the user on once they are authenticated, regardless of
 * where they were originally headed. The backend always redirects to `/dashboard`, so a
 * pending auth intent has to be resolved from here rather than from the intended route.
 */
function isAuthLandingPath(pathname: string) {
  return pathname === "/sign-in"
    || pathname === "/login"
    || pathname.startsWith("/onboarding")
    || pathname.startsWith("/dashboard")
    || pathname.startsWith("/create-event/");
}

/**
 * The routes whose elements are lazy, i.e. the only ones that can suspend the boundary
 * below. Kept separate from isAuthLandingPath: that one includes /sign-in, which uses
 * the public branded fallback and must never render the workspace shell.
 */
function isProtectedAppPath(pathname: string) {
  // /create-event/success is deliberately excluded: it is a single centred card, not the
  // workspace shell, so the sidebar skeleton would be a worse mismatch than showing
  // nothing. Its chunk is covered by the global loader instead -- CreateEventPage opens a
  // transition activity before navigating there and the page closes it on mount.
  if (pathname.startsWith("/create-event/success")) return false;
  // /onboarding is NOT the workspace shell — it is a single centred card. The dashboard-shell
  // skeleton is a layout mismatch there, and every path into /onboarding is already covered by
  // the bunny loader: FirstRunGate / AcceptInvitationPage open ONBOARDING_ENTRY_ACTIVITY before
  // navigating (welcome flow AND setup-restore), OnboardingPage's own bootstrap keeps it up, and
  // OnboardingPage ends it once painted. A skeleton flashing under that during the chunk fetch
  // is the seam we removed.
  if (pathname.startsWith("/onboarding")) return false;
  return pathname.startsWith("/dashboard")
    || pathname.startsWith("/create-event")
    || pathname.startsWith("/invitations/");
}

/** Key for the loader shown while FirstRunGate decides. Separate from ONBOARDING_ENTRY_ACTIVITY so
 * the two can overlap: this one ends when the query resolves, and if the decision is "redirect",
 * ONBOARDING_ENTRY_ACTIVITY is already open, so the bunny loader never blinks between them. */
const FIRST_RUN_GATE_ACTIVITY = "first-run-gate-decision";

/** New accounts get one guided first run. Once they defer, the persisted IN_PROGRESS state lets
 * them use the dashboard and resume from its checklist instead of trapping them in a redirect. */
function FirstRunGate({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const { user } = useAuth();
  const { data, isLoading, isError } = useQuery({
    queryKey: ONBOARDING_QUERY_KEY,
    queryFn: () => api.getOnboardingState(),
    enabled: Boolean(user),
    staleTime: 15_000,
    retry: false,
  });
  const gateActive = FIRST_TIME_ONBOARDING_ENABLED && isLoading;
  // While the gate is still deciding, show the app-wide bunny loader — the same one that covers
  // the redirect and the onboarding page's bootstrap — rather than the dashboard skeleton, which
  // was only ever right when "render the dashboard" was the certain outcome. The onboarding query
  // is usually warm (the dashboard fetched it), so on most navigations this window is a single
  // frame or skipped entirely.
  useEffect(() => {
    if (gateActive) {
      startTransitionActivity(FIRST_RUN_GATE_ACTIVITY, "immediate", "Getting BunnyCal ready");
      return () => endTransitionActivity(FIRST_RUN_GATE_ACTIVITY);
    }
    endTransitionActivity(FIRST_RUN_GATE_ACTIVITY);
  }, [gateActive]);

  if (!FIRST_TIME_ONBOARDING_ENABLED) return children;
  // Render nothing under the loader while the query is in flight. The effect above keeps the
  // bunny loader up; a skeleton here would sit behind it and flash as the outcome resolves.
  if (isLoading) return null;
  // An explicit setup errand — the "connect your calendar" mail an admin sends a member, or the
  // focused restore flow this gate itself sends people into — names the one thing to do and is not
  // a first run. Intercepting it would answer a request to fix one setting with a flow that asks
  // for a purpose and a published booking page, or loop the restore flow back on itself.
  const explicitSetupIntent = new URLSearchParams(location.search).has("setup");
  if (isError || explicitSetupIntent) return children;
  if (data?.status === "NOT_STARTED") {
    // Bridge the loader over the redirect + the onboarding chunk fetch + OnboardingPage's own
    // bootstrap, same as the setup-restore branch below. Without it the welcome flow flashed a
    // dashboard skeleton (Suspense fallback) then the onboarding spinner.
    startTransitionActivity(ONBOARDING_ENTRY_ACTIVITY, "immediate", "Setting up your account");
    return <Navigate to="/onboarding?step=purpose" replace state={{ from: location.pathname }} />;
  }
  // Someone who finished onboarding but has since lost a setting the destination genuinely needs
  // (cleared their availability, say) is routed to that one step — not the whole welcome flow.
  // Only the requirements this specific route depends on block it; settings/billing/integrations
  // and the dashboard root stay reachable so they can fix things. missingRequirements already
  // encodes every exemption, so an invitee is never held here for a calendar step they skip.
  const blocking = data
    ? getBlockingRequirements(location.pathname, data.missingRequirements ?? [])
    : [];
  if (blocking.length > 0 && data && !data.accountReady) {
    const returnTo = `${location.pathname}${location.search}`;
    const step = (data.resumeStep ?? "AVAILABILITY").toLowerCase();
    // Bridge the loader over the redirect + the onboarding page's own bootstrap. Calling this
    // during render is safe: startTransitionActivity de-dupes on the key, so re-renders before
    // the navigation commits do not stack activity. OnboardingPage ends it once painted.
    startTransitionActivity(ONBOARDING_ENTRY_ACTIVITY, "immediate", "Setting up your account");
    return (
      <Navigate
        to={`/onboarding?step=${step}&setup=1&returnTo=${encodeURIComponent(returnTo)}`}
        replace
        state={{ from: location.pathname }}
      />
    );
  }
  return children;
}

function ProtectedRoute({ children }: { children: JSX.Element }) {
  const location = useLocation();
  const { user, isHydratingAuth, authInitialized } = useAuth();

  if (isHydratingAuth && !authInitialized) {
    routeDebug("protected route waiting for hydration", {
      pathname: location.pathname,
      isHydratingAuth,
      authInitialized,
      hasUser: Boolean(user),
    });
    return null;
  }

  if (authInitialized && !user) {
    const target = `${location.pathname}${location.search}${location.hash}`;
    routeDebug("protected route redirecting to sign-in", { pathname: location.pathname, target, authInitialized });
    return <Navigate to={buildSignInUrl({ mode: "PROTECTED_ROUTE", returnTo: target })} replace />;
  }

  // A signed-in user who landed here only because OAuth always returns to /dashboard still
  // has a pending intent pointing at the page they actually asked for. Redirect during
  // render, before the landing page mounts — doing this in an effect lets the dashboard
  // paint for a frame first, which reads as a flash. The intent is consumed by the effect
  // in AppRoutes once we arrive, so it is only peeked at here.
  if (user && isAuthLandingPath(location.pathname)) {
    const pending = peekAuthIntent();
    if (pending) {
      const target = resolvePostLoginPath(pending);
      if (target !== getCurrentRelativeUrl()) {
        routeDebug("protected route deferring to pending auth intent", { pathname: location.pathname, target });
        return <Navigate to={target} replace />;
      }
    }
  }

  routeDebug("protected route allowed", { pathname: location.pathname, authInitialized, hasUser: Boolean(user) });
  return children;
}

function AppRoutes() {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const { user, isHydratingAuth, authInitialized } = useAuth();
  const oauthCallbackHandledRef = useRef(false);
  const signUpHandledRef = useRef(false);

  /* gtag is configured with send_page_view: false, so navigations have to be reported here or
     only the entry URL would ever be counted. Deliberately keyed on pathname alone: the query
     string carries OAuth tokens and checkout ids, and a ?checkout= round-trip should not read
     as a second visit to the same page. */
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location.pathname]);

  useEffect(() => {
    routeDebug("route state", {
      pathname: location.pathname,
      search: location.search,
      authInitialized,
      isHydratingAuth,
      hasUser: Boolean(user),
    });
  }, [authInitialized, isHydratingAuth, location.pathname, location.search, user]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const hash = url.hash.startsWith("#") ? new URLSearchParams(url.hash.slice(1)) : null;
    const accessToken = url.searchParams.get("accessToken") ?? hash?.get("accessToken");
    if (!accessToken || oauthCallbackHandledRef.current) return;

    oauthCallbackHandledRef.current = true;
    void (async () => {
      oauthDebug("access token found in callback URL", { pathname: url.pathname });
      setAccessToken(accessToken);
      url.searchParams.delete("accessToken");
      if (hash) hash.delete("accessToken");
      url.hash = hash && hash.toString() ? `#${hash.toString()}` : "";
      window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);

      const pendingIntent = peekAuthIntent();
      const redirected = resolvePostLoginPath(pendingIntent);
      queryClient.removeQueries({ queryKey: ME_QUERY_KEY, exact: true });
      try {
        await queryClient.fetchQuery(getMeQueryOptions());
      } catch (error) {
        oauthDebug("failed to hydrate /api/me after OAuth callback", {
          pathname: url.pathname,
          error: error instanceof Error ? error.message : "unknown",
        });
      }
      consumeAuthIntent();
      if (redirected !== getCurrentRelativeUrl()) {
        oauthDebug("redirecting post OAuth token extraction", { redirected });
        navigate(redirected, { replace: true });
      }
    })();
  }, [navigate, queryClient]);

  /* `sign_up` fires from an explicit backend marker only — never inferred from timestamps or
     "first time we saw this user" heuristics. OAuth2AuthenticationSuccessHandler appends
     ?newUser=1 to the redirect only when resolveOrCreateUser actually created the account this
     callback. Read once (the ref survives StrictMode's double-invoke) and stripped immediately,
     so a refresh or a bookmarked/shared URL can never replay it. */
  useEffect(() => {
    if (signUpHandledRef.current) return;
    const url = new URL(window.location.href);
    if (url.searchParams.get("newUser") !== "1") return;

    signUpHandledRef.current = true;
    url.searchParams.delete("newUser");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);

    const event = buildSignUpEvent("1");
    if (event) trackEvent(event.name, event.params);
  }, []);

  useEffect(() => {
    if (isHydratingAuth || !authInitialized || !user) return;
    const pending = peekAuthIntent();
    if (!pending) return;
    const current = getCurrentRelativeUrl();
    const pendingPath = resolvePostLoginPath(pending);
    if (pendingPath === current) {
      consumeAuthIntent();
      return;
    }

    if (!isAuthLandingPath(location.pathname)) return;

    const target = resolvePostLoginPath(consumeAuthIntent());
    if (target !== current) {
      routeDebug("navigating to pending auth intent", { current, target });
      navigate(target, { replace: true });
    }
  }, [authInitialized, isHydratingAuth, location.pathname, navigate, user]);

  return (
      /* Boundary for all lazy routes above. It wraps the whole table for simplicity;
         the landing element is synchronous and never suspends. The fallback is
         route-scoped: workspace routes get their structural skeleton, while public
         routes get a small branded mark instead of an unrelated dashboard shell. */
      <Suspense fallback={isProtectedAppPath(location.pathname) ? <DashboardRouteSkeleton /> : <PublicRouteFallback />}>
      <Routes>
        <Route path="/" element={<LandingPage />} />
        {/* /pricing is its own page rather than the full landing scroll: the
            SoftwareApplication/Offer and FAQPage structured data it publishes
            describes pricing, so pricing has to be what the page is actually
            about. It reuses the same section components, so it looks identical
            to the pricing block on the landing page. */}
        <Route path="/pricing" element={<PricingPage />} />
        <Route path="/calendly-alternative" element={<CalendlyAlternativePage />} />
        <Route path="/switch-from-calendly" element={<SwitchFromCalendlyPage />} />
        <Route path="/round-robin-scheduling" element={<RoundRobinSchedulingPage />} />
        <Route path="/about" element={<LandingPage />} />
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/contact" element={<ContactPage />} />
        <Route path="/docs/zoom" element={<ZoomIntegrationDocsPage />} />
        <Route path="/sign-in" element={<LoginPage />} />
        <Route path="/login" element={<Navigate to="/sign-in?mode=APP_LOGIN" replace />} />
        <Route path="/onboarding/*" element={<ProtectedRoute><ProtectedAppProviders><OnboardingPage /></ProtectedAppProviders></ProtectedRoute>} />
        <Route path="/create-event/success" element={<ProtectedRoute><CreateEventSuccessPage /></ProtectedRoute>} />
        <Route path="/create-event/*" element={<ProtectedRoute><FirstRunGate><ProtectedAppProviders><DashboardPage /></ProtectedAppProviders></FirstRunGate></ProtectedRoute>} />
        <Route path="/dashboard/*" element={<ProtectedRoute><FirstRunGate><ProtectedAppProviders><DashboardPage /></ProtectedAppProviders></FirstRunGate></ProtectedRoute>} />
        <Route path="/invitations/:token/accept" element={<ProtectedRoute><ProtectedAppProviders><AcceptInvitationPage /></ProtectedAppProviders></ProtectedRoute>} />
        <Route path="/settings/*" element={<ProtectedRoute><Navigate to="/dashboard/settings" replace /></ProtectedRoute>} />
        <Route path="/availability" element={<ProtectedRoute><Navigate to="/dashboard/availability" replace /></ProtectedRoute>} />
        <Route path="/bookings" element={<ProtectedRoute><Navigate to="/dashboard" replace /></ProtectedRoute>} />
        <Route path="/integrations" element={<ProtectedRoute><Navigate to="/dashboard/integrations" replace /></ProtectedRoute>} />
        <Route path="/book/:username/:eventTypeSlug" element={<PublicBookingRoute />} />
        <Route path="/public/:username/:eventTypeSlug" element={<PublicBookingRoute />} />
        <Route path="/book/:username/:eventTypeSlug/manage/:bookingId" element={<GuestManageBookingPage />} />
        <Route path="/public/:username/:eventTypeSlug/manage/:bookingId" element={<GuestManageBookingPage />} />
        <Route path="/:username/:eventTypeSlug/manage/:bookingId" element={<GuestManageBookingPage />} />
        <Route path="/manage/:bookingId" element={<GuestManageBookingPage />} />
        <Route path="/:username/:eventTypeSlug" element={<PublicBookingRoute />} />
        {/* Unknown routes render a 404 view rather than silently redirecting
            home — a mistyped link used to be indistinguishable from clicking
            "Home". See NotFoundPage for why the status stays 200. */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      </Suspense>
  );
}

export function App() {
  return (
    <AuthProvider>
      <UpgradePromptProvider>
        <GlobalApiLoader />
        <AppRoutes />
        {/* Single app-wide upgrade dialog; opened via useUpgradePrompt() from any gated surface. */}
        <UpgradePromptHost />
        {/* Self-hides once a choice is stored, and never renders when no analytics ID is set. */}
        <CookieConsent />
      </UpgradePromptProvider>
    </AuthProvider>
  );
}
